import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowLeft, ChevronLeft, ChevronRight, BookOpen } from '../lib/icons';
import { useBlogPostsListing, useBlogCategories } from '../hooks/useBlog';
import { createUniqueSlug } from '../utils/slugUtils';
import BlogCard from './BlogCard';
import SEO from './SEO';
import { getSEOConfig } from '../config/seoConfig';

const Blog: React.FC = () => {
  const seoData = getSEOConfig('/wissen');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedPostType, setSelectedPostType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  // useBlogPostsListing laedt alle 228 Posts, aber nur Listen-Felder (KEIN
  // HTML-content). Vorher useBlogPosts() ~1 MB Payload mit full content +
  // JOINs — fuer eine Karten-Listing-Seite uebertrieben.
  const { posts: blogPosts, loading, error } = useBlogPostsListing();
  const { blogCategories, loading: categoriesLoading } = useBlogCategories();
  const navigate = useNavigate();
  
  const itemsPerPage = 15;

  // Use categories from database
  const categories = ['Alle Kategorien', ...(blogCategories || []).map(cat => cat.name)];

  // Post types for filter
  const postTypes = [
    { value: '', label: 'Alle Typen' },
    { value: 'artikel', label: 'Artikel' },
    { value: 'news', label: 'News' },
    { value: 'fachbeitrag', label: 'Fachbeitrag' }
  ];

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = !selectedCategory || selectedCategory === 'Alle Kategorien' || post.category === selectedCategory;
    const matchesType = !selectedPostType || post.post_type === selectedPostType;
    return matchesSearch && matchesCategory && matchesType;
  });

  // Pagination
  const totalPages = Math.ceil(filteredPosts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPosts = filteredPosts.slice(startIndex, startIndex + itemsPerPage);

  // Reset page when search or category changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, selectedPostType]);

  // Scroll to top when page changes (but not on initial load)
  React.useEffect(() => {
    if (currentPage > 1) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage]);

  const handleBlogPostClick = (postId: string) => {
    const post = blogPosts.find(p => p.id === postId);
    if (post) {
      const slug = createUniqueSlug(post.title, post.id);
      navigate(`/wissen/${slug}`);
    }
  };

  if (loading || categoriesLoading) {
    return (
      <>
        <SEO
          title={seoData.title}
          description={seoData.description}
          keywords={seoData.keywords}
          canonical={seoData.canonical}
        />
        <section className="py-8 md:py-16 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-8 md:py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Lade Artikel und Kategorien...</p>
          </div>
        </div>
      </section>
      </>
    );
  }

  if (error) {
    return (
      <>
        <SEO
          title={seoData.title}
          description={seoData.description}
          keywords={seoData.keywords}
          canonical={seoData.canonical}
        />
        <section className="py-8 md:py-16 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-8 md:py-12">
            <p className="text-red-600">Fehler beim Laden der Artikel: {error}</p>
          </div>
        </div>
      </section>
      </>
    );
  }

  return (
    <>
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
      />
      <section className="py-8 md:py-16 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4 md:mb-6 transition-colors text-sm md:text-base"
        >
          <ArrowLeft className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" />
          <span className="hidden sm:inline">Zurück zur Startseite</span>
          <span className="sm:hidden">Zurück</span>
        </button>

        {/* Header */}
        <div className="text-center mb-6 md:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 md:mb-4">
            Gesundheitswissen
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Aktuelle Gesundheitstipps und medizinische Informationen von unseren Experten
          </p>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Artikel durchsuchen..."
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            <div className="sm:w-48">
              <select
                value={selectedPostType}
                onChange={(e) => setSelectedPostType(e.target.value)}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
              >
                {postTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Filter Info */}
          {(searchTerm || selectedCategory !== 'Alle Kategorien' || selectedPostType) && (
            <div className="text-sm text-gray-600 pt-4 border-t border-gray-200">
              {filteredPosts.length} von {blogPosts.length} Artikeln gefunden
              {searchTerm && ` für "${searchTerm}"`}
              {selectedCategory && selectedCategory !== 'Alle Kategorien' && ` in "${selectedCategory}"`}
              {selectedPostType && ` vom Typ "${postTypes.find(t => t.value === selectedPostType)?.label}"`}
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('Alle Kategorien');
                  setSelectedPostType('');
                }}
                className="ml-3 text-blue-600 hover:text-blue-800 underline"
              >
                Filter zurücksetzen
              </button>
            </div>
          )}
        </div>

        {/* Results Info */}
        <div className="text-center mb-6">
          <p className="text-gray-600">
            {filteredPosts.length} {filteredPosts.length === 1 ? 'Artikel gefunden' : 'Artikel gefunden'}
            {searchTerm && ` für "${searchTerm}"`}
            {selectedCategory && selectedCategory !== 'Alle Kategorien' && ` in "${selectedCategory}"`}
          </p>
          {totalPages > 1 && (
            <p className="text-sm text-gray-500 mt-1">
              Seite {currentPage} von {totalPages}
            </p>
          )}
        </div>

        {/* Blog Posts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {paginatedPosts.map((post) => (
            <BlogCard
              key={post.id}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => handleBlogPostClick(post.id)}
              post={post}
            />
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center space-x-4 mb-8">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Zurück
            </button>
            
            <div className="flex items-center space-x-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Weiter
              <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          </div>
        )}

        {/* No Results */}
        {paginatedPosts.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Keine Artikel gefunden
            </h3>
            <p className="text-gray-600 mb-8">
              {searchTerm || selectedCategory !== 'Alle Kategorien' || selectedPostType
                ? 'Für die ausgewählten Filter wurden keine passenden Artikel gefunden.'
                : 'Noch keine Artikel verfügbar.'}
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('');
                setSelectedPostType('');
                setCurrentPage(1);
              }}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Filter zurücksetzen
            </button>
          </div>
        )}
      </div>
    </section>
    </>
  );
};

export default Blog;